import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FeatureFlagsManagerService } from 'src/app/services/util/feature-flags-manager.service';
import { FeatureFlag } from 'src/app/models/feature-flag.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-feature-flag',
  templateUrl: './feature-flag.component.html',
  styleUrls: ['./feature-flag.component.scss'],
  standalone: false
})
export class FeatureFlagComponent implements OnInit {
  private featureFlagsManager = inject(FeatureFlagsManagerService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  featureFlags: FeatureFlag[] = [];
  isLoading = false;
  error: string | null = null;
  currentTenant: string | null = null;
  currentInstance: string | null = null;
  isInstanceMode = false;
  displayedColumns: string[] = ['name', 'id', 'description', 'status'];

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const tenantId = params['id'];
        const instanceId = params['instanceId'];
        if (instanceId) {
          this.isInstanceMode = true;
          this.currentInstance = instanceId;
          this.featureFlagsManager.loadInstanceFeatureFlags(instanceId);
        } else if (tenantId) {
          this.isInstanceMode = false;
          this.currentTenant = tenantId;
          this.featureFlagsManager.loadFeatureFlags(tenantId);
        }
        this.setupSubscriptions();
      });
  }

  private setupSubscriptions(): void {
    this.featureFlagsManager.featureFlags$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(flags => {
        this.featureFlags = flags;
      });

    this.featureFlagsManager.isLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(loading => {
        this.isLoading = loading;
      });

    this.featureFlagsManager.error$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(error => {
        this.error = error;
      });

    this.featureFlagsManager.currentTenant$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tenant => {
        this.currentTenant = tenant;
      });
  }

  getEnabledCount(): number {
    return this.featureFlags.filter(f => this.featureFlagsManager.isFeatureEnabled(f.id)).length;
  }

  getDisabledCount(): number {
    return this.featureFlags.filter(f => !this.featureFlagsManager.isFeatureEnabled(f.id)).length;
  }

  isFeatureFlagEnabled(flagId: string): boolean {
    return this.featureFlagsManager.isFeatureEnabled(flagId);
  }

  refresh(): void {
    if (this.isInstanceMode && this.currentInstance) {
      this.featureFlagsManager.loadInstanceFeatureFlags(this.currentInstance);
    } else if (!this.isInstanceMode && this.currentTenant) {
      this.featureFlagsManager.loadFeatureFlags(this.currentTenant);
    }
  }
}
